/**
 * 파편 풀 (data-model §3, FR-004a/b/c).
 * DebrisPoolLogic(순수 규칙) + Rapier 바디 + DebrisRenderer(인스턴싱)를 묶는다.
 * 파괴 시 사전 분할 청크로 rigid body를 스폰하고, 수명/상한 규칙에 따라 페이드아웃 후
 * 바디를 재사용(제거 후 풀 반환).
 */
import * as THREE from 'three';
import type { BreakableObjectDef } from '../core/types';
import type { PhysicsWorld } from '../physics/PhysicsWorld';
import { DebrisRenderer } from '../render/DebrisRenderer';
import { debrisPaletteFor, pickPaletteEntry, type PaletteEntry } from '../render/models';
import { DebrisPoolLogic } from './debrisLogic';
import { fracturePrimitive, type ChunkDesc } from '../physics/fracture';
import { BALANCE } from '../config/balance';

const FADE_DURATION = 0.5;

interface DebrisEntry {
  body: import('@dimforge/rapier3d-compat').RigidBody;
  fadeStart: number | null;
}

export class DebrisPool {
  private logic = new DebrisPoolLogic();
  renderer: DebrisRenderer;
  private cache = new Map<string, ChunkDesc[]>();
  private entries = new Map<number, DebrisEntry>();
  private tmpOffset = new THREE.Vector3();

  constructor(
    private physics: PhysicsWorld,
    scene: THREE.Scene,
  ) {
    this.renderer = new DebrisRenderer(scene);
  }

  /** 살아있는 파편 수(페이드 중 포함) — 유휴 렌더 게이팅의 활동 신호(main.ts 배선). */
  get count(): number {
    return this.entries.size;
  }

  private chunksFor(def: BreakableObjectDef): ChunkDesc[] {
    let chunks = this.cache.get(def.id);
    if (!chunks) {
      chunks = fracturePrimitive(def.primitive, def.fragmentCount);
      this.cache.set(def.id, chunks);
    }
    return chunks;
  }

  /** 파괴 지점에서 파편 버스트 스폰. variant는 파편 팔레트 분기(모니터 code/browser). */
  spawnBurst(
    def: BreakableObjectDef,
    worldPos: THREE.Vector3,
    worldQuat: THREE.Quaternion,
    impactDir: THREE.Vector3,
    now: number,
    variant?: string,
    impactSpeed = 0,
  ): void {
    const chunks = this.chunksFor(def);
    const massEach = Math.max(0.05, def.mass / chunks.length);
    const dir = impactDir.lengthSq() > 1e-6 ? impactDir.clone().normalize() : new THREE.Vector3(0, 1, 0);
    const palette: PaletteEntry[] = debrisPaletteFor(def, variant);
    // 세게 부술수록 파편이 더 멀리·힘차게 튄다(임팩트 세기에 비례, 상한 clamp).
    const fi = BALANCE.fragmentImpulse;
    const scale = Math.min(fi.speedScaleMax, Math.max(1, impactSpeed / fi.speedRef));

    for (const chunk of chunks) {
      // 오브젝트 재질 팔레트에서 파편 프로파일·색 샘플.
      const entry = pickPaletteEntry(palette);
      if (!this.renderer.hasFreeSlot(entry.profile)) continue; // 해당 프로파일 슬롯 소진
      const { id, evicted } = this.logic.spawn(now);
      for (const ev of evicted) this.beginFade(ev.id, now); // 용량 초과분 페이드아웃

      // 청크 월드 위치 = worldPos + worldQuat * chunkCenter
      this.tmpOffset.set(chunk.center[0], chunk.center[1], chunk.center[2]).applyQuaternion(worldQuat);
      const px = worldPos.x + this.tmpOffset.x;
      const py = worldPos.y + this.tmpOffset.y;
      const pz = worldPos.z + this.tmpOffset.z;

      const body = this.physics.addDynamicBody({ x: px, y: py, z: pz }, false); // 파편은 CCD 불필요
      body.setRotation({ x: worldQuat.x, y: worldQuat.y, z: worldQuat.z, w: worldQuat.w }, false);
      this.physics.attachCuboid(
        body,
        { x: chunk.size[0] / 2, y: chunk.size[1] / 2, z: chunk.size[2] / 2 },
        massEach,
        { kind: 'debris', debrisId: id },
        false, // 파편 콜라이더 자체는 이벤트 off(파편끼리 n² 스팸 방지) — 클래터(debris:impact)는
        //        COLLISION_EVENTS가 켜진 정적 콜라이더와의 쌍으로 수신된다(PhysicsWorld.addStaticCuboid).
      );

      // 임팩트 방향 + 산포로 튕김 (임팩트 세기에 비례해 더 능동적으로).
      const jitter = fi.jitter * scale;
      const speed = fi.base * scale;
      body.setLinvel(
        {
          x: dir.x * speed + (Math.random() - 0.5) * jitter,
          // 항상 위로 튀어오르는 팝(up) + 임팩트 상향분 — 파편이 시원하게 솟구침.
          y: fi.up * scale + Math.abs(dir.y) * speed * 0.5 + Math.random() * jitter,
          z: dir.z * speed + (Math.random() - 0.5) * jitter,
        },
        true,
      );
      // 회전도 세기에 비례(빙글빙글 더 활발하게).
      const spin = 10 * scale;
      body.setAngvel(
        {
          x: (Math.random() - 0.5) * spin,
          y: (Math.random() - 0.5) * spin,
          z: (Math.random() - 0.5) * spin,
        },
        true,
      );

      this.renderer.add(id, entry.profile, chunk.size, entry.color);
      this.entries.set(id, { body, fadeStart: null });
    }
  }

  private beginFade(debrisId: number, now: number): void {
    const e = this.entries.get(debrisId);
    if (e && e.fadeStart === null) e.fadeStart = now;
  }

  /** 매 프레임: 수명 만료 처리 + 트랜스폼 동기화 + 페이드 완료 반환. */
  update(now: number): void {
    // 수명 만료 → 페이드 시작.
    for (const ev of this.logic.update(now)) this.beginFade(ev.id, now);

    // 직접 순회 — Map은 순회 중 현재 키 delete(destroy)가 안전하므로 spread 복사(매 프레임
    // 파편 수만큼의 튜플 할당 → GC 압박)가 불필요하다.
    for (const [id, e] of this.entries) {
      const t = e.body.translation();
      const r = e.body.rotation();
      let fade = 1;
      if (e.fadeStart !== null) {
        const k = (now - e.fadeStart) / FADE_DURATION;
        if (k >= 1) {
          this.destroy(id);
          continue;
        }
        fade = 1 - k;
      }
      this.renderer.setTransform(id, t, r, fade);
    }
    this.renderer.flush();
  }

  private destroy(id: number): void {
    const e = this.entries.get(id);
    if (!e) return;
    this.physics.removeBody(e.body);
    this.renderer.remove(id);
    this.entries.delete(id);
    this.logic.free(id);
  }

  /** 리셋: 모든 파편 즉시 제거. */
  clear(): void {
    for (const id of [...this.entries.keys()]) this.destroy(id);
    this.logic.clear();
    this.renderer.clear();
  }
}
